import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';

import { WizardValidationError } from '@/types/Wizard';
import { formatCurrency } from '@/utils/currency';
import { RapidStorSettings } from '@/utils/rapidstor';

import CircleCheckbox from '@/components/elements/circle-checkbox';
import ErrorTag from '@/components/elements/error-tag';

import { computedInsurancePremium } from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

interface Props {
    selectedCoverage: number;
    setSelectedCoverage: (arg: number) => void;
    handleButtonClick: (arg: number) => void;
    formErrors: WizardValidationError[];
    coverages: InsuranceCoverage[];
    handleValueChange: (value: string) => void;
    form: Record<string, string>;
    update: (arg: Record<string, string> | DraftFunction<Record<string, string>>) => void;
    taxRate: number;
}

export default function Picker({
    selectedCoverage,
    setSelectedCoverage,
    handleButtonClick,
    formErrors,
    coverages,
    handleValueChange,
    form,
    update,
    taxRate
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const insurancePremium = computedInsurancePremium(coverages, selectedCoverage, taxRate);

    return (
        <>
            {formErrors?.map((error) => (
                <ErrorTag key={`${error.name}_error_idx`} error={error} />
            ))}

            <div className={`${classes.insurance_picker}`}>
                <div>
                    <button
                        disabled={selectedCoverage === -1}
                        onClick={() => {
                            if (selectedCoverage > 0) {
                                handleButtonClick(selectedCoverage - 1);
                                setSelectedCoverage(selectedCoverage - 1);
                            } else {
                                handleButtonClick(-1);
                                setSelectedCoverage(-1);
                            }
                        }}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 16 16'
                            fill='currentColor'
                        >
                            <path d='M3.75 7.25a.75.75 0 0 0 0 1.5h8.5a.75.75 0 0 0 0-1.5h-8.5Z' />
                        </svg>
                    </button>
                </div>
                <div>
                    {selectedCoverage !== -1 ? (
                        <>
                            <div className={`${classes.coverage}`}>
                                <span>
                                    {formatCurrency(
                                        insurancePremium,
                                        location,
                                        true
                                    )}
                                </span>
                                <span> = {t('wizard:insuranceCoverage:pickerText')}</span>
                                <span>{location.rapidstor.currencySymbol}{coverages[selectedCoverage].dcCoverage}</span>
                            </div>
                            <span className={`${classes.total_coverage}`}>{`${location.rapidstor.currencySymbol}${coverages[selectedCoverage].dcCoverage
                                } ${t('wizard:insuranceCoverage.coverage')}`}</span>
                        </>
                    ) : (
                        <div className={`${classes.coverage}`}>
                            <span>
                                {t('wizard:insuranceCoverage:noInsuranceSelected')}
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <button
                        disabled={selectedCoverage === coverages.length - 1}
                        onClick={() => {
                            if (selectedCoverage < coverages.length - 1) {
                                handleButtonClick(selectedCoverage + 1);
                                setSelectedCoverage(selectedCoverage + 1);
                            }
                        }}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            viewBox='0 0 16 16'
                            fill='currentColor'
                        >
                            <path d='M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z' />
                        </svg>
                    </button>
                </div>
            </div>

            {RapidStorSettings(location).insurance().manualEntry().show() && (
                <div className={`${classes.manualEntry}`}>
                    <span className={`${classes.label}`}>
                        {t('wizard:insuranceCoverage:manualEntryLabel')}
                    </span>

                    <div className={`${classes.input_group}`}>
                        <span className={`${classes.prefix}`}>
                            {location.rapidstor.currencySymbol}
                        </span>
                        <input
                            type='number'
                            min='0'
                            value={form.declaredValueOfGoods}
                            onChange={(e) => {
                                handleValueChange(e.target.value);
                                update((draft) => {
                                    draft.declaredValueOfGoods = e.target.value;
                                });
                            }}
                            className={`${classes.input}`}
                        />
                    </div>
                </div>
            )}

            {(RapidStorSettings(location).insurance().required() || form.insuranceCoverage !== '') && (
                <CircleCheckbox
                    formKey={'declaredValueOfGoodsAgree'}
                    value={form.declaredValueOfGoodsAgree}
                    label={
                        location.rapidstor.wizards.moveinSingle.insuranceAcceptanceText
                    }
                    update={update}
                />
            )}

            {RapidStorSettings(location).insurance().optional() && form.insuranceCoverage === '' && location.rapidstor.wizards.moveinSingle.insuranceRefusedText && (
                <CircleCheckbox
                    formKey={'declaredValueOfGoodsAgree'}
                    value={form.declaredValueOfGoodsAgree}
                    label={
                        location.rapidstor.wizards.moveinSingle.insuranceRefusedText
                    }
                    update={update}
                />
            )}
        </>
    );
}
